/**
 * Coppock Curve
 *
 * Long-term momentum indicator originally designed for monthly charts.
 * WMA of (ROC(long) + ROC(short)).
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const coppockIndicator: IndicatorDefinition = {
  id: "coppock",
  name: "Coppock Curve",
  shortName: "COPP",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "wma", label: "WMA Period", type: "number", default: 10, min: 2, max: 100 },
    { key: "roc1", label: "ROC Period 1", type: "number", default: 14, min: 2, max: 200 },
    { key: "roc2", label: "ROC Period 2", type: "number", default: 11, min: 2, max: 200 },
    { key: "color", label: "Color", type: "color", default: "#00BCD4" },
  ],
  plots: [
    { key: "coppock", label: "Coppock", color: "#00BCD4", style: "line", lineWidth: 2, priceScaleId: "coppock" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { wma, roc1, roc2 } = params;
    const maxRoc = Math.max(roc1, roc2);
    const data: { time: number; value: number }[] = [];

    if (candles.length < maxRoc + wma) return [{ key: "coppock", data }];

    // ROC values
    const rocSum: number[] = [];
    for (let i = maxRoc; i < candles.length; i++) {
      const r1 = candles[i - roc1].close !== 0
        ? ((candles[i].close - candles[i - roc1].close) / candles[i - roc1].close) * 100 : 0;
      const r2 = candles[i - roc2].close !== 0
        ? ((candles[i].close - candles[i - roc2].close) / candles[i - roc2].close) * 100 : 0;
      rocSum.push(r1 + r2);
    }

    // WMA of rocSum
    for (let i = wma - 1; i < rocSum.length; i++) {
      let sum = 0, weightSum = 0;
      for (let j = 0; j < wma; j++) {
        const w = j + 1;
        sum += rocSum[i - wma + 1 + j] * w;
        weightSum += w;
      }
      data.push({ time: candles[i + maxRoc].time, value: sum / weightSum });
    }

    return [{ key: "coppock", data }];
  },
};
